import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageProvider';
import ProBadge from '@/components/ProBadge';
import BrandLogo from '@/components/brand/BrandLogo';
import { appUrl, talkUrl } from '@/config/hosts';
import '@/styles/vani-landing.css';

export default function SiteFooter() {
  const { t } = useLanguage();
  return (
    <footer className="vani-site-footer" role="contentinfo">
      <div className="vani-wrap">
        <div className="vani-fgrid">
          {/* Brand & Mission Column */}
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 14, minWidth: 0 }}>
              <BrandLogo className="vani-brand" tone="inverse" />
              <ProBadge />
            </div>
            <p style={{ maxWidth: '32ch', lineHeight: 1.65, marginTop: 12, color: 'rgba(209, 226, 215, 0.85)', fontSize: '13px' }}>
              {t.footerBlurb || 'An oncology companion for patients and families, in the language you think in. Clinical navigation, expert second opinions, and AI care support.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              <div className="vani-built-in">
                <span>Built for patients, wherever care takes them.</span>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '11.5px', color: '#6ee7b7', fontWeight: 500 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', display: 'inline-block', boxShadow: '0 0 6px #34d399' }} />
                <span>24/7 AI Companion Online</span>
              </div>
              <div style={{ marginTop: 4 }}>
                <a
                  href="mailto:care@getbyonco.com"
                  style={{ fontSize: '12.5px', color: 'rgba(209, 226, 215, 0.75)', textDecoration: 'underline', textUnderlineOffset: 3 }}
                >
                  care@getbyonco.com
                </a>
              </div>
            </div>
          </div>

          {/* Col 2: Clinical Services */}
          <div>
            <h4>Clinical Care</h4>
            <ul>
              <li><Link to="/second-opinion">Second Opinion</Link></li>
              <li><Link to="/teleconsultation">Teleconsultation</Link></li>
              <li><Link to="/journey-builder">{t.journeyBuilder || 'Journey Builder'}</Link></li>
              <li><Link to="/find-cancer-centers">Find Cancer Centers</Link></li>
              <li><Link to="/find-oncologists">Find Oncologists</Link></li>
              <li><Link to="/cost-calculator">{t.costCalculator || 'Cost Calculator'}</Link></li>
            </ul>
          </div>

          {/* Col 3: Vani AI Companion */}
          <div>
            <h4>Vani AI</h4>
            <ul>
              <li><a href={talkUrl('/')}>{t.talk || 'Voice & Chat Navigator'}</a></li>
              <li><a href="/#how">{t.how || 'How Vani Works'}</a></li>
              <li><a href="/#languages">{t.languages || '18 Languages'}</a></li>
              <li><a href="/#scope">{t.coverage || 'Clinical Safety & Scope'}</a></li>
              <li><Link to="/specialday">Special Day Support</Link></li>
              <li><Link to="/subscribe">{t.pricing || 'Pro Membership'}</Link></li>
              <li><Link to="/lifetime-draw">Lifetime Draw</Link></li>
            </ul>
          </div>

          {/* Col 4: Resources & Clinicians */}
          <div>
            <h4>Resources</h4>
            <ul>
              <li>
                <Link to="/oncoagent" className="vani-footer-clinician-entry">
                  <span>For Doctors: OncoAgent</span>
                  <span className="vani-footer-chip">Clinicians</span>
                </Link>
              </li>
              <li><Link to="/for-hospitals">{t.forHospitals || 'For Hospitals'}</Link></li>
              <li><Link to="/cancer">{t.cancerGuides || 'Cancer Guides'}</Link></li>
              <li><Link to="/rare-cancers">{t.rareCancers || 'Rare Cancers Directory'}</Link></li>
              <li><Link to="/cancer-centers-by-state">US Cancer Centers</Link></li>
              <li><Link to="/platform">{t.marketplace || 'Care Marketplace'}</Link></li>
              <li><Link to="/about">About ByOnco</Link></li>
            </ul>
          </div>

          {/* Col 5: Trust & Compliance */}
          <div>
            <h4>Trust & Legal</h4>
            <ul>
              <li><Link to="/privacy">{t.privacy || 'Privacy Policy'}</Link></li>
              <li><Link to="/terms-and-conditions">{t.terms || 'Terms of Service'}</Link></li>
              <li><Link to="/health-privacy">Health Data Privacy</Link></li>
              <li><Link to="/medical-disclaimer">Medical Disclaimer</Link></li>
              <li><Link to="/security">{t.security || 'Security & Compliance'}</Link></li>
              <li><Link to="/cancellation-refund">Cancellation & Refunds</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Fine Print & Credentials */}
        <div className="vani-fine">
          <div className="vani-fine-cred">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>{t.trainedBy || 'Trained by oncologists and health care professionals · Evidence-based guidelines'}</span>
          </div>
          <p style={{ margin: '6px 0 0', lineHeight: 1.65 }}>
            {t.footerDisclaimer || 'ByOnco and Vani provide supportive oncology information and clinical navigation. They do not replace professional medical advice, clinical diagnosis, or individualized treatment plans. Always consult your oncology team regarding medical decisions.'}
          </p>
          {t.footerEmergency ? (
            <p className="vani-fine-emergency">{t.footerEmergency}</p>
          ) : (
            <p className="vani-fine-emergency">In a US medical emergency, call 911 immediately; for 24/7 crisis support call or text 988.</p>
          )}
          <div className="vani-fine-copy">
            <div>
              © 2026 ByOnco, a product of Praesidio Care Private Limited. All rights reserved.
            </div>
            <div className="vani-fine-right-group">
              <Link to="/oncoagent" className="vani-footer-clinician-pill" title="OncoAgent: AI Early Cancer Referral for Doctors & Clinicians">
                <span className="vani-footer-pulse-dot" aria-hidden="true" />
                <span>For Doctors & Clinicians: <strong>OncoAgent →</strong></span>
              </Link>
              <div className="vani-fine-badges">
                <span>🔒 Encrypted & Confidential</span>
                <span className="vani-fine-dot">•</span>
                <span>HIPAA-Ready Architecture</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
